import type { ZoneRulesTable } from '@/lib/time'

/**
 * Reference data — the tables the Flight Engine reads before it judges
 * anything. BUSINESS-RULES section 11 calls this the most dangerous part of
 * the system: a wrong row here makes every constraint check wrong without
 * anybody noticing. Hence the audit fields on every row and the versioned
 * seed behind them.
 */

/** Where a row as the user currently sees it came from. */
export type RowOrigin = 'seed' | 'added' | 'edited'

/**
 * Carried by every reference row, whatever the table.
 *
 * `rowOrigin` rather than `origin`: a city pair has an origin station, and a
 * metadata field that shadows a domain field would be silently omitted by
 * `FieldsOf`.
 */
export interface RowState {
  /** Stable key inside the table. */
  id: string
  rowOrigin: RowOrigin
  /** ISO 8601 UTC. Null while the row is untouched seed data. */
  updatedAt: string | null
  /** Email of whoever last changed it. Null for untouched seed data. */
  updatedBy: string | null
}

export interface CountryFields {
  /** ISO 3166-1 alpha-2. */
  code: string
  name: string
}

export interface TimezoneFields {
  /** IANA zone name, e.g. "Asia/Ho_Chi_Minh". The key flights resolve through. */
  name: string
  countryCode: string
  /** Abbreviation in force at the start of the shipped window. */
  abbreviation: string
  /** Seconds east of UTC at the start of the window. */
  baseOffset: number
  /** True when at least one clock change falls inside the window. */
  observesDst: boolean
  /** Clock changes inside the window. */
  transitionCount: number
  /** Distinct offsets the zone takes inside the window. */
  distinctOffsets: number
}

export interface AirportFields {
  iata: string
  /** OurAirports ident. Often a real ICAO code, sometimes a local code. */
  icao: string
  name: string
  continent: string
  countryCode: string
  municipality: string
  /** IANA zone name. Joins to the timezone table. */
  timezone: string
  type: string
  /** DOM for Vietnam, ITL for everywhere else. */
  scope: string
}

/** `unset` is not `no`: nobody has said, so nobody has denied it either. */
export type AllowState = 'yes' | 'no' | 'unset'

/**
 * One column of a permission matrix: group, category and type together.
 *
 * Type alone is not an aircraft. "CEO" spans the A320, the A321 and the A330,
 * and the operator's own limits treat those as three different aircraft — all
 * 8 A330 tails may serve India where no A320 or A321 CEO tail may. "A320" is
 * ambiguous the other way: it is a group of 14 CEO tails and also the
 * category of the 8 wet-leased A32W tails. Only the trio identifies an
 * aircraft, so the trio is what a matrix column addresses.
 */
export interface FleetColumn {
  /** `group/category/type`. What every matrix cell and every exception keys on. */
  key: string
  group: string
  category: string
  type: string
  /** Tails in the fleet carrying this trio. */
  tails: number
}

/**
 * One row of a permission matrix, keyed by `FleetColumn.key`.
 *
 * The columns are data, not a fixed union: they are the distinct trios in
 * `aircraft` and change when the fleet does, so a row may be missing a key
 * the fleet has acquired. A missing key reads as `unset`.
 */
export type AllowRow = Record<string, AllowState>

/** The row of a city pair matrix that applies wherever no pair matches. */
export const DEFAULT_RULE = 'DEFAULT'

/** Vietnam is domestic, everywhere else is international. */
export const AIRPORT_SCOPES = ['DOM', 'ITL'] as const
export type AirportScope = (typeof AIRPORT_SCOPES)[number]

/** Longest a group code may be. Short enough to read in a matrix row. */
export const GROUP_CODE_MAX = 7

/**
 * A named set of stations, or of countries — addressable anywhere one of its
 * members would be.
 *
 * DOM, ITL and LIMITED were views on the airport screen; they are groups, and
 * so is anything the operator names themselves. A rule written against a
 * group covers every member and keeps covering them as membership changes,
 * which is the point: "north of the country" said once beats eleven station
 * rules that drift apart.
 *
 * A group is NEVER interchangeable with a station or a country code, because
 * the code spaces overlap — DOM is also Dominica's IATA code, AS is also
 * American Samoa. Every place a group can be named therefore records which
 * kind it is rather than guessing from the string.
 */
export interface GroupFields {
  /** Up to GROUP_CODE_MAX characters, uppercase. */
  code: string
  name: string
  /**
   * Derivation that computes the members, or '' for an explicit list.
   *
   *   scope:DOM      airports whose scope column reads DOM
   *   capability     airports carrying a capability rule of their own
   *   continent:AS   countries with an airport on that continent
   *   not:VN         every country except the ones named
   *
   * A derived group cannot go stale: it is recomputed from the tables it
   * names every time it is read.
   */
  rule: string
  /** Members of a list group — IATA or ISO codes. Empty when `rule` is set. */
  members: string[]
  /** Built in: membership is derived, and the row cannot be deleted. */
  builtin: boolean
}

/** What a capability row or a tail limit is addressing. */
export type SubjectKind = 'station' | 'country' | 'group'

export interface AircraftFields {
  /** Registration, e.g. "VN-A321". The Gantt row key. */
  regn: string
  /** A321 · A320 · A330 · WL. */
  group: string
  /** ICAO type designator, e.g. A21N. */
  category: string
  /**
   * Aircraft type: NEO ACT · NEO non ACT · Thomas · CEO · C909 · A32W.
   *
   * The dimension both permission matrices are keyed by. It is the source
   * sheet's fifth column, the one headed with the Vietnamese for "content".
   */
  type: string
  /** As written by the operator, e.g. "Y240", "12C365Y". */
  seatConfig: string
  /** Cabin letter -> seats, parsed from seatConfig. */
  cabins: Record<string, number>
  /** Total seats. Drives R5: seats >= booked passengers. */
  seats: number
  /** Limit 1 — most flight cycles in an operating day. 0 = no limit. */
  maxCyclesPerDay: number
  /**
   * Limit 2 — longest block time the tail may work, in minutes. 0 = no limit.
   *
   * A ceiling: a tail carrying 120 may not work a leg longer than two hours.
   * The NOTE text in the source sheet reads the other way round and is stale
   * (confirmed with OMC, 10/09/2026).
   */
  maxBlockMinutes: number
  /** Limit 3 — ISO country codes the tail may NOT serve. */
  limitCountry: string[]
  /** Limit 4 — IATA codes the tail may NOT serve. Empty in the first drop. */
  limitAirport: string[]
  /** Performance rank. 1 is the highest; a lower number substitutes for a higher. */
  rank: number
  /** Operator's own wording, kept verbatim. */
  note: string
}

export interface CityPairFields {
  /**
   * Station sequence joined by "-": SGN-PER-SGN, SGN-DPS-PER-DPS-SGN.
   *
   * Also the key the capability matrix rows are addressed by, so a pair needs
   * no group of its own.
   */
  key: string
  shape: 'oneway' | 'return' | 'return_techstop'
  origin: string
  /** Turnaround point — the far end of the pair. */
  turnaround: string
  /** One techstop per direction, empty when the leg is direct. */
  outboundVia: string
  inboundVia: string
}

export interface CityPairCapabilityFields {
  /** A city pair key, or DEFAULT_RULE for everything without its own row. */
  cityPairKey: string
  allow: AllowRow
}

export interface CountryCapabilityFields {
  /** ISO country code, or a country group code. */
  subject: string
  subjectKind: 'country' | 'group'
  allow: AllowRow
  /** [tails permitted, tails carrying the trio] behind each verdict. */
  tails: Record<string, [number, number]>
}

export interface AirportCapabilityFields {
  /** IATA code, or an airport group code. */
  subject: string
  subjectKind: 'station' | 'group'
  allow: AllowRow
}

/** Which table an exception hangs off. */
export const EXCEPTION_SCOPES = ['cityPair', 'country', 'airport'] as const
export type ExceptionScope = (typeof EXCEPTION_SCOPES)[number]

/**
 * One ad-hoc departure from a capability baseline.
 *
 * The three matrices are the standing rule and sit lowest. An exception is
 * made on the City pairs, Countries or Airports screen against one subject,
 * and overrides the baseline for the columns it names — only those. A cell
 * that matches the baseline again is not an exception, so it is dropped on
 * save rather than stored as a no-op; anything left is a real deviation and
 * carries who made it, when, and why.
 */
export interface CapabilityExceptionFields {
  scope: ExceptionScope
  /** City pair key, ISO country code or IATA code, per the scope. */
  subject: string
  /** Only the columns that differ from the baseline. */
  allow: AllowRow
  reason: string
}

export type Country = CountryFields & RowState
export type Timezone = TimezoneFields & RowState
export type Airport = AirportFields & RowState
export type Aircraft = AircraftFields & RowState
export type CityPair = CityPairFields & RowState
export type CityPairCapability = CityPairCapabilityFields & RowState
export type CountryCapability = CountryCapabilityFields & RowState
export type AirportCapability = AirportCapabilityFields & RowState
export type CapabilityException = CapabilityExceptionFields & RowState
export type AirportGroup = GroupFields & RowState
export type CountryGroup = GroupFields & RowState

/** Table name -> row type. Keeps the provider generic without losing types. */
export interface ReferenceRowMap {
  country: Country
  timezone: Timezone
  airport: Airport
  aircraft: Aircraft
  cityPair: CityPair
  cityPairCapability: CityPairCapability
  countryCapability: CountryCapability
  airportCapability: AirportCapability
  capabilityException: CapabilityException
  airportGroup: AirportGroup
  countryGroup: CountryGroup
}

export type ReferenceTableName = keyof ReferenceRowMap

export const REFERENCE_TABLES = [
  'country',
  'timezone',
  'airport',
  'aircraft',
  'cityPair',
  'cityPairCapability',
  'countryCapability',
  'airportCapability',
  'capabilityException',
  'airportGroup',
  'countryGroup',
] as const

export type FieldsOf<T extends ReferenceTableName> = Omit<ReferenceRowMap[T], keyof RowState>

/** Provenance of the shipped seed, from tools/build-reference-data.py. */
export interface ReferenceManifest {
  generatedAt: string
  window: { from: string; to: string }
  counts: Record<
    'country' | 'timezone' | 'timezoneTransition' | 'airport' | 'aircraft' | 'cityPair',
    number
  >
  /** Distinct group/category/type trios — the columns of every matrix. */
  fleetColumns: FleetColumn[]
  integrity: {
    airportTimezoneNotInTable: string[]
    airportWithoutTimezone: string[]
    airportCountryNotInTable: string[]
    duplicateIata: string[]
    cityPairStationNotInTable: string[]
    capabilityUnset: string[]
    capabilityWithoutCityPair: string[]
    capabilityWithoutAirport: string[]
    aircraftWithoutType: string[]
    limitCountryNotInTable: string[]
    /** Groups whose derivation or list currently matches nothing. */
    emptyGroup: string[]
    /** Group codes that also exist as a station or country code. */
    groupCodeCollision: string[]
  }
}

/** Stable codes so the UI owns the wording, mirroring AuthError. */
export type ReferenceErrorCode =
  | 'not_found'
  | 'duplicate_key'
  | 'invalid_value'
  | 'seed_unavailable'
  | 'storage_full'
  | 'unknown'

export class ReferenceError extends Error {
  code: ReferenceErrorCode
  /** Field the error belongs to, when it belongs to one. */
  field?: string
  constructor(code: ReferenceErrorCode, field?: string) {
    super(code)
    this.name = 'ReferenceError'
    this.code = code
    this.field = field
  }
}

/**
 * Shared contract for every provider.
 *
 * Same rule as the auth layer (CODE-RULES 3.3): swapping the local provider
 * for Supabase, or later for an in-house backend, must not touch a single
 * line of UI. Writes here are UI-level only — the server re-checks every one
 * of them, since `settings.edit` in permissions.ts hides buttons and nothing
 * more.
 */
export interface ReferenceProvider {
  readonly name: string
  list<T extends ReferenceTableName>(table: T): Promise<ReferenceRowMap[T][]>
  create<T extends ReferenceTableName>(
    table: T,
    fields: FieldsOf<T>,
    actor: string,
  ): Promise<ReferenceRowMap[T]>
  update<T extends ReferenceTableName>(
    table: T,
    id: string,
    fields: Partial<FieldsOf<T>>,
    actor: string,
  ): Promise<ReferenceRowMap[T]>
  /** Soft delete. BUSINESS-RULES 4.2: reference rows are never hard-deleted. */
  remove<T extends ReferenceTableName>(table: T, id: string, actor: string): Promise<void>
  /** Offset table for the clock. Loaded separately from the zone list. */
  zoneRules(): Promise<ZoneRulesTable>
  manifest(): Promise<ReferenceManifest>
  /** Drop every local change and fall back to the shipped seed. */
  reset(): Promise<void>
  /** How many rows differ from the seed, per table. */
  pendingChanges(): Promise<Record<ReferenceTableName, number>>
}
